/* eslint-disable no-underscore-dangle --
 * The `__reset*ForTests` export follows the same convention as
 * `analytics/hubspot.ts` and b2b-api's `__resetRateLimiterForTests`.
 */

/**
 * Cloudflare Turnstile — script loader and site-key resolution.
 *
 * Turnstile protects the public Request Access form, which is the one
 * unauthenticated write path on the site (it provisions a WorkOS organization
 * and sends real invitation emails). The widget is a UX affordance only:
 * enforcement is entirely server-side in b2b-api, which verifies the token
 * against siteverify before any side effect.
 *
 * No npm wrapper. This mirrors `shared/analytics/hubspot.ts` — a hand-rolled
 * ~20-line injector is the house pattern for third-party scripts, and a wrapper
 * would add a React peer-range to keep in step for no behaviour we need.
 */

export interface TurnstileRenderOptions {
  sitekey: string;
  action?: string;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'flexible' | 'compact';
  /**
   * Turnstile otherwise injects a hidden `cf-turnstile-response` input into the
   * enclosing <form>. We POST JSON through `apiClient`, so inside a
   * react-hook-form-managed form that input is dead weight.
   */
  'response-field'?: boolean;
  callback?: (token: string) => void;
  /** Return true to suppress Cloudflare's own error UI and render our own. */
  'error-callback'?: (code?: string) => boolean | void;
  'expired-callback'?: () => void;
  'timeout-callback'?: () => void;
  'unsupported-callback'?: () => void;
}

export interface TurnstileApi {
  render: (
    el: HTMLElement,
    options: TurnstileRenderOptions
  ) => string | undefined;
  reset: (widgetId?: string) => void;
  remove: (widgetId?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

/**
 * Cloudflare's published always-passes test key. Public and documented — it is
 * not a credential, and it only ever issues the dummy token
 * `XXXX.DUMMY.TOKEN.XXXX`, which b2b-api accepts only when it holds the
 * matching test *secret*.
 */
export const TURNSTILE_TEST_SITE_KEY = '1x00000000000000000000AA';

const SCRIPT_ID = 'cf-turnstile-script';

/**
 * `render=explicit` stops the script auto-scanning the document for
 * `.cf-turnstile` elements, and makes `render()` return the widget id we need
 * in order to `reset()` after a failed submit. Tokens are single-use, so
 * without that id a retry would silently replay a spent token.
 */
const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/**
 * The only hostname on the production widget's allowed-domains list in
 * Cloudflare. Turnstile refuses to issue a token on any other host (error
 * 110200), so this list and the Cloudflare dashboard must agree.
 *
 * Deliberately NOT the two-host list in `shared/api/config.ts`:
 * `client-portal.internal.thredup.com` also serves this bundle but is not
 * allowlisted, and `client-portal.thredup.com` is the only URL exposed to
 * users. The internal host therefore falls through to the test key below and
 * degrades to a legible server-side rejection, rather than the permanently
 * disabled submit button that handing it the production key would produce.
 */
const PRODUCTION_HOSTS = ['client-portal.thredup.com'];

/**
 * CI builds ONE artifact and deploys it to staging and production both
 * (.github/workflows/main.yml), so `VITE_PUBLIC_TURNSTILE_SITE_KEY` is baked
 * identically into every environment and cannot distinguish them. Switching on
 * the browser hostname is how the rest of the app solves this — see
 * `getApiBaseUrl` in shared/api/config.ts.
 *
 * Read lazily rather than frozen at module load, which keeps it unit-testable
 * (`vi.stubEnv` works, no `resetModules` dance) and prerender-safe by
 * construction rather than by guard.
 */
export const getTurnstileSiteKey = (): string | undefined => {
  // Prerender: no hostname to switch on, and no widget is ever rendered in Node
  // because that only happens in an effect. The test key keeps the emitted
  // markup the same shape as the client's first paint.
  if (typeof window === 'undefined') return TURNSTILE_TEST_SITE_KEY;

  return PRODUCTION_HOSTS.includes(window.location.hostname)
    ? (import.meta.env.VITE_PUBLIC_TURNSTILE_SITE_KEY as string | undefined)
    : TURNSTILE_TEST_SITE_KEY;
};

let loader: Promise<TurnstileApi> | null = null;

/**
 * Injects the Turnstile script once and resolves with its global API.
 *
 * Memoized because React StrictMode double-invokes effects in development and
 * the form may mount more than once per page. The memo is cleared on failure so
 * a later mount can retry — a blocked script is usually an extension or a
 * corporate proxy, and both can change between mounts.
 */
export const loadTurnstile = (): Promise<TurnstileApi> => {
  if (typeof document === 'undefined') {
    return Promise.reject(new Error('Turnstile requires a browser'));
  }
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (loader) return loader;

  loader = new Promise<TurnstileApi>((resolve, reject) => {
    // A tag can survive a previous failed load, because clearing the memo
    // below does not remove it. Drop it first so this stays idempotent.
    document.getElementById(SCRIPT_ID)?.remove();

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;

    // `load` rather than the `?onload=` query param: api.js defines
    // window.turnstile synchronously while it evaluates, so it is present by
    // this event, and the param would force us to leak a callback name onto
    // window just to hear about it.
    //
    // Deliberately NOT turnstile.ready(): that exists for callers whose code
    // runs BEFORE api.js, and calling it afterwards logs
    // "turnstile.ready() would break if called *before* the ... script is
    // loaded" and never invokes the callback — so render() never ran. Once the
    // load event has fired the API is usable directly.
    script.addEventListener('load', () => {
      const api = window.turnstile;
      if (!api) {
        loader = null;
        reject(new Error('Turnstile loaded without defining window.turnstile'));
        return;
      }
      resolve(api);
    });

    script.addEventListener('error', () => {
      loader = null;
      reject(new Error('Turnstile script failed to load'));
    });

    document.head.appendChild(script);
  });

  return loader;
};

/** Test hook: module state outlives `vi.resetModules()` within a file. */
export const __resetTurnstileLoaderForTests = (): void => {
  loader = null;
};
